import React, {Suspense, useEffect} from 'react';
import {format, parseISO} from 'date-fns';
import {
  Grid,
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Loading from '../Loading/Loading';
import {ErrorPage} from '../../pages/ErrorPage';
import {HistoryDiffDetails} from '../../schema-types/HistoryDiffDetails';
import {HistoryQueryType, useHistoryDiff} from '../AccountReviewDialog/useHistoryDiff';
import {themeColors} from '../../theme/Theme';
import {Panel} from '../Panel/Panel';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {GlobalPermissionEnum, OVModelsEnum} from '../../schema-types/globalTypes';
import {useAccountContext} from '../../contexts/AccountContext';

type AccountHistoryProps = {
  entityId: string;
  entityType: OVModelsEnum;
};

function AccountHistory({entityId, entityType}: AccountHistoryProps) {
  const {onGetHistoryDiff, data, loading, error} = useHistoryDiff(
    HistoryQueryType.GENERIC
  );

  useEffect(() => {
    const variables = {entityId, entityType};
    onGetHistoryDiff({variables});
  }, [onGetHistoryDiff, entityId, entityType]);

  if (loading) return <Loading />;

  if (error) {
    return (
      <ErrorPage code="500" text="There was an issue loading Account Changes History" />
    );
  }

  const groupedByDate = data?.reduce<Record<string, HistoryDiffDetails[]>>(
    (acc: Record<string, HistoryDiffDetails[]>, item: HistoryDiffDetails) => {
      const date = item.date || 'Unknown Date';
      acc[date] = acc[date] || [];
      acc[date].push(item);
      return acc;
    },
    {}
  );

  return (
    <Grid item xs={12} data-testid="account-history-table">
      <Panel color={themeColors.lightPink} title="Account Changes History">
        <Box mb={4}>
          {data && data.length === 0 ? (
            <Typography variant="body1" sx={{textAlign: 'center', mt: 2}}>
              No change history data yet exists.
            </Typography>
          ) : (
            groupedByDate &&
            Object.entries(groupedByDate).map(([date, records]) => {
              const formattedDate = date
                ? format(parseISO(date), "EEEE do MMMM yyyy 'at' HH:mm")
                : 'Unknown Date';
              const primaryUser = records[0].user || 'Unknown User';
              return (
                <TableContainer component={Paper} key={date} sx={{mb: 2}}>
                  <Paper
                    sx={{
                      borderTop: '3px solid rgb(95, 200, 235)',
                      p: 1,
                      backgroundColor: '#f5f5f5',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{textAlign: 'center'}}
                    >{`Changes made by ${primaryUser} on ${formattedDate}`}</Typography>
                  </Paper>
                  <Table aria-label="history table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{width: '16%'}}>Field</TableCell>
                        <TableCell sx={{width: '16%'}}>Old Value</TableCell>
                        <TableCell sx={{width: '16%'}}>New Value</TableCell>
                        <TableCell sx={{width: '16%'}}>Reason</TableCell>
                        <TableCell sx={{width: '16%'}}>Record</TableCell>
                        <TableCell sx={{width: '16%'}}>Extra</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {records.map((record: HistoryDiffDetails) => (
                        <TableRow
                          key={`${record.field}-${record.date}-${record.newValue}`}
                        >
                          <TableCell>{record.field}</TableCell>
                          <TableCell>{record.oldValue}</TableCell>
                          <TableCell>{record.newValue}</TableCell>
                          <TableCell>{record.reason}</TableCell>
                          <TableCell>{record.record}</TableCell>
                          <TableCell>{record.extra}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })
          )}
        </Box>
      </Panel>
    </Grid>
  );
}

export default AccountHistory;

export const AccountHistoryPage: React.FC = () => {
  const {accountId} = useAccountContext();

  if (!accountId) {
    return null;
  }

  return (
    <FeatureBoundary
      features={[GlobalPermissionEnum.can_access_admin_features]}
      showErrorIfDenied={false}
    >
      <Suspense fallback={<Loading />}>
        <AccountHistory
          data-testid="account-history"
          entityType={OVModelsEnum.ACCOUNT}
          entityId={accountId}
        />
      </Suspense>
    </FeatureBoundary>
  );
};
